import React, { useState } from 'react';
import { UserPlus, UserMinus, Trash2, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export function AdminUsersTab({ 
  users, courses, 
  assignDialogOpen, setAssignDialogOpen,
  selectedUser, setSelectedUser,
  selectedCourse, setSelectedCourse,
  assignAction, setAssignAction,
  handleCourseAssignment,
  handleDeleteUser,
  handleInviteUser
}) {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');

  const onInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteStatus('');
    const result = await handleInviteUser(inviteEmail);
    if (result.success) {
      setInviteStatus('success');
      setInviteEmail('');
      setTimeout(() => { setInviteDialogOpen(false); setInviteStatus(''); }, 2000);
    } else {
      setInviteStatus(result.error || 'Failed to send invitation');
    }
    setInviteLoading(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
          User Management
        </h1>
        <div className="flex items-center gap-4">
          <Dialog open={inviteDialogOpen} onOpenChange={(open) => { setInviteDialogOpen(open); if (!open) setInviteStatus(''); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="invite-user-btn">
                <Mail className="w-4 h-4 mr-2" /> Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141417] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-white">Invite a New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={onInviteSubmit} className="space-y-4 mt-4">
                <p className="text-[#A1A1AA] text-sm">
                  Send a branded invitation email with a registration link.
                </p>
                <div>
                  <Label className="text-[#A1A1AA]">Email Address</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="input-dark mt-2"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                {inviteStatus === 'success' && (
                  <p className="text-[#00FF66] text-sm">Invitation sent successfully!</p>
                )}
                {inviteStatus && inviteStatus !== 'success' && (
                  <p className="text-[#FF3333] text-sm">{inviteStatus}</p>
                )}
                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={inviteLoading}
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? 'Sending...' : 'Send Invitation'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <span className="text-[#71717A]">{users.length} users</span>
        </div>
      </div>
      
      <div className="bg-[#141417] border border-[#27272A] rounded-md overflow-hidden">
        <table className="data-table">
          <thead>
            <tr className="bg-[#09090B]">
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Enrolled Courses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id || u.email} data-testid={`user-row-${u.email}`}>
                <td className="text-white">{u.name}</td>
                <td className="text-[#A1A1AA] font-mono text-sm">{u.email}</td>
                <td>
                  <span className={`px-2 py-1 rounded-sm text-xs ${
                    u.role === 'admin' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#27272A] text-[#A1A1AA]'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="text-[#A1A1AA]">{u.enrolled_courses?.length || 0}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <Dialog open={assignDialogOpen && selectedUser?.id === u.id} onOpenChange={(open) => {
                      setAssignDialogOpen(open);
                      if (!open) setSelectedUser(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-[#007AFF]"
                          onClick={() => setSelectedUser(u)}
                          data-testid={`manage-user-${u.email}`}
                        >
                          Manage Courses
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#141417] border-[#27272A]">
                        <DialogHeader>
                          <DialogTitle className="text-white">Manage Courses for {u.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label className="text-[#A1A1AA]">Action</Label>
                            <Select value={assignAction} onValueChange={setAssignAction}>
                              <SelectTrigger className="input-dark mt-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#141417] border-[#27272A]">
                                <SelectItem value="assign">Assign Course</SelectItem>
                                <SelectItem value="remove">Remove Course</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[#A1A1AA]">Course</Label>
                            <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                              <SelectTrigger className="input-dark mt-2">
                                <SelectValue placeholder="Select course" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#141417] border-[#27272A]">
                                {courses.map(c => (
                                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button 
                            className="btn-primary w-full" 
                            onClick={handleCourseAssignment}
                            data-testid="confirm-assignment-btn"
                          >
                            {assignAction === 'assign' ? <UserPlus className="w-4 h-4 mr-2" /> : <UserMinus className="w-4 h-4 mr-2" />}
                            {assignAction === 'assign' ? 'Assign' : 'Remove'} Course
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    {u.role !== 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#FF3333] hover:text-[#FF3333] hover:bg-[#FF3333]/10"
                        onClick={() => handleDeleteUser(u.id, u.email)}
                        data-testid={`delete-user-${u.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
